// src/store/errorTexts.ts
import { jobTypeLabel } from '@/features/ai/lib/jobTypeLabel'
import { fieldLabel, styleText } from '@/features/instructions/lib/errorMessage'
import { reviewInProgressText } from '@/features/moderation/lib/reviewInProgress'
import { getUiLang, messagesNow } from '@/i18n'
import { messages } from './messages'

type Msgs = (typeof messages)['ru']
type Text = (m: Msgs, detail: Record<string, unknown>, endpoint: string) => string

/**
 * Код api ловится и голым, и с префиксом сущности: `CustomBaseException` пишет код как
 * `{entity}_{code}` (`instruction_not_author`, `gamified_lesson_blocks_invalid_gamified_block`).
 */
export const matchCode = (code: string, key: string): boolean =>
  code === key || code.endsWith('_' + key)

/**
 * Тексты по коду отказа api. Ключ — код без префикса сущности; при нескольких совпадениях
 * берётся самый длинный ключ, поэтому уточнение для одной сущности пишется полным кодом.
 * `Map`, а не литерал: код приходит сырой строкой, и `constructor` у литерала нашёлся бы.
 */
const CODE_TEXT = new Map<string, Text>([
  ['not_author', (m) => m.notAuthor],
  ['not_admin', (m) => m.notAdmin],
  ['invalid_gamified_block', (m) => m.invalidBlock],
  ['gamified_lesson_blocks_invalid_gamified_block', (m) => m.blockNotAllowed],
  ['review_in_progress', (_m, _d, endpoint) => reviewInProgressText(endpoint)],
  [
    'studio_job_in_progress',
    (m, detail) => m.jobInProgress(jobTypeLabel(detail.job_type as string | undefined)),
  ],
  ['draft_course', (m) => m.draftCourse],
  ['trial_lessons_limit', (m) => m.trialLessonsLimit],
  ['mascots_limit', (m) => m.mascotsLimit],
  ['invalid_style_value', (_m, detail) => styleText(detail)],
  ['article_in_use', (m) => m.articleInUse],
  ['article_not_published', (m) => m.articleNotPublished],
  ['not_published', (m) => m.notPublished],
  ['published', (m) => m.published],
  ['category_in_use', (m) => m.categoryInUse],
])

/**
 * Отказы, которые объясняет сам экран, вызвавший ручку: тост поверх задвоил бы его.
 * `409 {error: 'already_fresh'}` у перевода статьи — «Перевод актуален» от `useTranslation`.
 */
const SELF_EXPLAINED = ['already_fresh']

const STATUS_TEXT = new Map<number, (m: Msgs) => string>([
  [400, (m) => m.status400],
  [403, (m) => m.status403],
  [404, (m) => m.status404],
  [409, (m) => m.status409],
])

const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

const codeText = (code: string, detail: unknown, endpoint: string): string | null => {
  let best: string | null = null
  for (const key of CODE_TEXT.keys())
    if (matchCode(code, key) && key.length > (best?.length ?? 0)) best = key
  if (best === null) return null
  const text = CODE_TEXT.get(best) as Text
  return text(messagesNow(messages), asRecord(detail), endpoint)
}

/** Pydantic-отказ `{detail: [{type, loc, ctx}]}`: понятный текст только для знакомых типов. */
const validationText = (detail: unknown): string | null => {
  if (!Array.isArray(detail) || detail.length === 0) return null
  const first = asRecord(detail[0])
  const loc = Array.isArray(first.loc) ? first.loc : []
  const field = fieldLabel(String(loc[loc.length - 1] ?? ''))
  const m = messagesNow(messages)
  if (first.type === 'category_kind_mismatch') return m.categoryKindMismatch
  if (first.type === 'string_too_long') {
    const max = asRecord(first.ctx).max_length
    return typeof max === 'number' ? m.fieldTooLongMax(field, max) : m.fieldTooLong(field)
  }
  return null
}

// Сервер пишет `detail` по-английски и технически; автору показываем его, только если он
// уже по-русски — строкой или полем `detail`/`message` объекта отказа.
const CYRILLIC = /[а-яё]/i
const russianDetail = (detail: unknown): string | null => {
  // Русский текст сервера в английском интерфейсе не показываем: дальше сработает текст по статусу.
  if (getUiLang() !== 'ru') return null
  const rec = asRecord(detail)
  for (const v of [detail, rec.detail, rec.message])
    if (typeof v === 'string' && CYRILLIC.test(v)) return v
  return null
}

/**
 * Текст тоста на отказ api: сначала по коду, затем русский `detail` сервера, затем общий
 * текст статуса. `null` — тоста нет: отказ объясняет экран или для статуса текста нет
 * (422 с незнакомой валидацией — молча, а не «Проверьте поля»).
 */
export const errorText = (
  status: number,
  code: string | null,
  endpoint: string,
  detail?: unknown,
): string | null => {
  if (code && SELF_EXPLAINED.some((key) => matchCode(code, key))) return null
  const byCode = code ? codeText(code, detail, endpoint) : null
  if (byCode) return byCode
  if (status === 422) return validationText(detail) ?? russianDetail(detail)
  const m = messagesNow(messages)
  return (
    russianDetail(detail) ?? (status >= 500 ? m.server : (STATUS_TEXT.get(status)?.(m) ?? null))
  )
}

/**
 * Текст отказа сделан под этот случай (код, самообъясняющий отказ, распознанная
 * валидация 422), а не общий: ID запроса рядом нужен только общим текстам. Русский
 * `detail` сервера и тексты по статусу/5xx — общие.
 */
export const isSpecificErrorText = (
  status: number,
  code: string | null,
  detail?: unknown,
): boolean => {
  if (code && SELF_EXPLAINED.some((key) => matchCode(code, key))) return true
  if (code && codeText(code, detail, '') !== null) return true
  return status === 422 && validationText(detail) !== null
}
